<script setup lang="ts">
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import type { CreditBalance } from '@/types/newsletter'

// Presentational only: the balance is fetched once by the parent (OrganizerNavBar)
// so that mounting several chips never fires duplicate requests.
defineProps<{
  balance: CreditBalance | null
  loading: boolean
}>()

const { t } = useI18n()

const router = useRouter()
const { currentOrganisation } = useAuth()

function goToCredits(): void {
  const slug = currentOrganisation.value?.slug
  if (slug) {
    void router.push(`/organizer/organisations/${slug}/credits`)
  }
}
</script>

<template>
  <button
    v-if="currentOrganisation"
    type="button"
    class="inline-flex items-center gap-1.5 border border-slate-600 hover:border-emerald-500/60 rounded-lg px-3 py-2 text-sm transition-colors"
    :class="balance && balance.balance < 100
      ? 'text-amber-400 border-amber-500/30 hover:border-amber-500/60'
      : 'text-slate-300 hover:text-white'"
    :title="t('credits.chip.title')"
    @click="goToCredits"
  >
    <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
    <span class="font-medium">
      <span v-if="loading && balance === null">-</span>
      <span v-else>{{ t('credits.chip.balance_label', { balance: balance?.balance.toLocaleString() ?? '-' }) }}</span>
    </span>
  </button>
</template>
